import {
  DatePicker,
  DateRangePicker,
  DateTimePicker,
  type DatePickerValue,
  type DateRangeValue,
  type ThemeVariables,
} from "@novinstack/react-datepicker";
import { CalendarDays, Clock3 } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { useUiStore } from "../store/ui-store";

type FieldChrome = {
  label?: string;
  hint?: string;
  error?: string;
  className?: string;
  required?: boolean;
};

type DateFieldProps = FieldChrome & {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  clearable?: boolean;
};

type DateRangeFieldProps = FieldChrome & {
  start?: string | null;
  end?: string | null;
  onChange: (range: { start: string; end: string }) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
};

type DateTimeFieldProps = FieldChrome & {
  value?: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minDateTime?: string;
  maxDateTime?: string;
  clearable?: boolean;
};

const isDateValue = (value: unknown): value is DatePickerValue =>
  Boolean(value && typeof value === "object" && "iso" in value && "timestamp" in value);

const isRangeValue = (value: unknown): value is DateRangeValue =>
  Boolean(value && typeof value === "object" && "start" in value && "end" in value);

function FieldShell({ label, hint, error, className, required, icon, children }: FieldChrome & { icon: ReactNode; children: ReactNode }) {
  return (
    <div className={cx("field lia-date-field", error && "is-invalid", className)}>
      {label ? (
        <div className="lia-date-field__label-row">
          <span className="field__label">{label}{required ? <b aria-hidden="true"> *</b> : null}</span>
          <span className="lia-date-field__icon" aria-hidden="true">{icon}</span>
        </div>
      ) : null}
      {children}
      {error ? <small className="field__error">{error}</small> : hint ? <small className="field__hint">{hint}</small> : null}
    </div>
  );
}

function useLiaDatePickerTheme() {
  const theme = useUiStore((state) => state.theme);

  return useMemo<{ themeName: "minimal" | "midnight"; variables: ThemeVariables }>(() => {
    if (theme === "dark") {
      return {
        themeName: "midnight",
        variables: {
          primary: "#43B6BB",
          primaryHover: "#56C0C5",
          primaryActive: "#349BA0",
          primaryContrast: "#041518",
          secondary: "#D7A66F",
          softAccent: "#163F43",
          background: "#081F22",
          surface: "#0C292D",
          text: "#EAF2F3",
          muted: "#8EA6AA",
          border: "#2B5054",
          range: "#163F43",
          disabled: "#6E878B",
          focus: "rgba(67, 182, 187, .24)",
          shadow: "0 28px 80px rgba(0, 0, 0, .42)",
          radius: 16,
          width: "100%",
          maxWidth: "100%",
          inputHeight: 48,
          calendarWidth: 318,
          daySize: 38,
          fontSize: 14,
          fontFamily: '"Yekan Bakh FaNum", Tahoma, sans-serif',
          zIndex: 22000,
        },
      };
    }

    return {
      themeName: "minimal",
      variables: {
        primary: "#143A40",
        primaryHover: "#20464A",
        primaryActive: "#051D20",
        primaryContrast: "#FFFFFF",
        secondary: "#E3A663",
        softAccent: "#FFF0E0",
        background: "#FFFFFF",
        surface: "#F2F7F8",
        text: "#161D1E",
        muted: "#66777A",
        border: "#C8D9DC",
        range: "#D2EFF2",
        disabled: "#A7B5B7",
        focus: "rgba(56, 173, 178, .18)",
        shadow: "0 24px 72px rgba(1, 11, 14, .16)",
        radius: 16,
        width: "100%",
        maxWidth: "100%",
        inputHeight: 48,
        calendarWidth: 318,
        daySize: 38,
        fontSize: 14,
        fontFamily: '"Yekan Bakh FaNum", Tahoma, sans-serif',
        zIndex: 22000,
      },
    };
  }, [theme]);
}


function formatTehranTime(value?: string | null) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "Asia/Tehran",
  }).format(date);
}

function commonPickerProps(themeName: "minimal" | "midnight", variables: ThemeVariables) {
  return {
    calendar: "jalali" as const,
    locale: "fa-IR" as const,
    numerals: "persian" as const,
    theme: themeName,
    variables,
    className: "lia-datepicker",
    calendarClassName: "lia-datepicker__calendar",
    inputClassName: "lia-datepicker__input",
    displayFormat: "D MMMM YYYY",
    valueType: "object" as const,
    allowTyping: true,
    strictValidation: true,
    clearable: true,
    showToday: true,
    todayIndicator: "soft-fill" as const,
    selectionShape: "rounded" as const,
    weekStartsOn: 6 as const,
    monthsToShow: { desktop: 1, tablet: 1, mobile: 1 },
    mobileMode: "sheet" as const,
    placement: "bottom-start" as const,
    autoFlip: true,
    offsetY: 8,
    positionStrategy: "reposition" as const,
    portal: true,
  };
}

export function LiaDateField({
  value,
  onChange,
  label,
  hint,
  error,
  className,
  required,
  placeholder = "انتخاب تاریخ",
  disabled,
  minDate,
  maxDate,
  clearable = true,
}: DateFieldProps) {
  const { themeName, variables } = useLiaDatePickerTheme();

  return (
    <FieldShell label={label} hint={hint} error={error} className={className} required={required} icon={<CalendarDays size={15} />}>
      <DatePicker
        {...commonPickerProps(themeName, variables)}
        value={value || null}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        minDate={minDate || undefined}
        maxDate={maxDate || undefined}
        clearable={clearable}
        onChange={(next) => onChange(isDateValue(next) ? next.iso : "")}
        ariaLabel={label || "انتخاب تاریخ"}
        errorMessage={error}
      />
    </FieldShell>
  );
}

export function LiaDateRangeField({
  start,
  end,
  onChange,
  label = "بازه تاریخ",
  hint,
  error,
  className,
  required,
  placeholder = "انتخاب بازه زمانی",
  disabled,
  clearable = true,
}: DateRangeFieldProps) {
  const { themeName, variables } = useLiaDatePickerTheme();

  return (
    <FieldShell label={label} hint={hint} error={error} className={cx("lia-date-field--range", className)} required={required} icon={<CalendarDays size={15} />}>
      <DateRangePicker
        {...commonPickerProps(themeName, variables)}
        value={{ start: start || undefined, end: end || undefined }}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        clearable={clearable}
        rangeHoverPreview
        rangeInputMode="single"
        onChange={(next) => {
          if (!isRangeValue(next)) {
            onChange({ start: "", end: "" });
            return;
          }
          onChange({ start: next.start?.iso ?? "", end: next.end?.iso ?? "" });
        }}
        ariaLabel={label}
        errorMessage={error}
      />
    </FieldShell>
  );
}

export function LiaDateTimeField({
  value,
  onChange,
  label,
  hint,
  error,
  className,
  required,
  placeholder = "انتخاب تاریخ و ساعت",
  disabled,
  minDateTime,
  maxDateTime,
  clearable = true,
}: DateTimeFieldProps) {
  const { themeName, variables } = useLiaDatePickerTheme();
  const selectedTime = useMemo(() => formatTehranTime(value), [value]);

  return (
    <FieldShell label={label} hint={hint} error={error} className={className} required={required} icon={<Clock3 size={15} />}>
      <DateTimePicker
        {...commonPickerProps(themeName, variables)}
        value={value || null}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        minDateTime={minDateTime || undefined}
        maxDateTime={maxDateTime || undefined}
        clearable={clearable}
        displayFormat="D MMMM YYYY"
        timeFormat={24}
        timeStep={5}
        timePickerVariant="select"
        timezone="Asia/Tehran"
        confirmOnSelect
        onChange={(next) => onChange(isDateValue(next) ? new Date(next.timestamp).toISOString() : "")}
        ariaLabel={label || "انتخاب تاریخ و ساعت"}
        errorMessage={error}
      />
      {selectedTime ? (
        <div className="lia-date-time-value" aria-live="polite">
          <Clock3 size={14} aria-hidden="true" />
          <span>ساعت انتخاب‌شده</span>
          <strong>{selectedTime}</strong>
          <small>تهران</small>
        </div>
      ) : null}
    </FieldShell>
  );
}
